/* eslint-disable libraries/no-restricted-imports */
import { getAnalytics, isSupported } from 'firebase/analytics';
import type { FirebaseApp } from 'firebase/app';
import { initializeApp } from 'firebase/app';
import type { RemoteConfig } from 'firebase/remote-config';
import { getRemoteConfig, fetchAndActivate, getBoolean } from 'firebase/remote-config';

/* eslint-enable libraries/no-restricted-imports */

import _ from 'lodash';
import Config from '@util/config';
import { logger } from '@util/observability';

interface FirebaseAppOptions {
    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    // googleAnalyticsId is being used for GA4 Data Collection
    // measurementId is still required for Firebase remoteConfig
    measurementId?: string;
}

const FirebaseAppConfigBase: Omit<FirebaseAppOptions, 'appId' | 'measurementId'> = {
    apiKey: Config.FIREBASE_API_KEY,
    authDomain: 'gamechanger-push-notifications.firebaseapp.com',
    databaseURL: 'https://gamechanger-push-notifications.firebaseio.com',
    projectId: 'gamechanger-push-notifications',
    storageBucket: 'gamechanger-push-notifications.appspot.com',
    messagingSenderId: '376889844239',
};

interface State {
    app: FirebaseApp | undefined;
    initialized: boolean;
}
const state: State = { app: undefined, initialized: false };

export const initialize = async (defaultValues: RemoteConfig['defaultConfig']) => {
    const config: FirebaseAppOptions = _.merge({}, FirebaseAppConfigBase, {
        appId: Config.FIREBASE_APP_ID,
        measurementId: Config.FIREBASE_MEASUREMENT_ID,
    });

    state.app = initializeApp(config);

    const supported = await isSupported();
    if (supported) {
        getAnalytics();
        const remoteConfig: RemoteConfig = getRemoteConfig(state.app);

        if (Config.FIREBASE_FETCH_INTERVAL_MILLIS > 0) {
            remoteConfig.settings.minimumFetchIntervalMillis = Config.FIREBASE_FETCH_INTERVAL_MILLIS;
        }

        remoteConfig.defaultConfig = defaultValues;

        return fetchAndActivate(remoteConfig)
            .then((_activated) => (state.initialized = true))
            .catch((error) => {
                logger.error('Error initializing Firebase: ', { error, context: { errorType: 'firebase' } });
                return false;
            });
    } else {
        return Promise.resolve(false);
    }
};

export const ifInitializedValue = (flag: string, defaultValue: boolean): boolean => {
    const config: FirebaseAppOptions = _.merge({}, FirebaseAppConfigBase, {
        appId: Config.FIREBASE_APP_ID,
        measurementId: Config.FIREBASE_MEASUREMENT_ID,
    });

    const remoteConfig: RemoteConfig = getRemoteConfig(state.app || initializeApp(config));

    if (state.initialized) {
        return getBoolean(remoteConfig, flag);
    } else {
        return defaultValue;
    }
};
